import classNames from '@sindresorhus/class-names';
import Script from 'next/script';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import styles from './VideoPlayer.module.scss';

export const VideoPlayer = ({
  autoPlay,
  videoUrl,
  videoObject,
  analyticsEvent,
  className,
  dataTestid,
  previewImageUrl,
}: {
  autoPlay: boolean;
  videoUrl: string;
  videoObject?: object;
  analyticsEvent?: () => void;
  className?: string;
  dataTestid?: string;
  previewImageUrl?: string;
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const forceMutedRef = useRef(false);
  const [isPlayerInitialized, setPlayerInitialized] = useState(false);

  useEffect(() => {
    const handlePlayerEvent = (event: MessageEvent) => {
      if (event.origin !== 'https://vk.com') return;

      let data;
      try {
        data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (error) {
        console.error('Error parsing event data:', error);
        return;
      }

      if (data.event === 'inited') {
        setPlayerInitialized(true);
      }

      if (data.event === 'started' || data.event === 'inited') {
        if (!forceMutedRef.current) {
          forceMutedRef.current = true;
          (window as any).VK?.VideoPlayer(iframeRef.current).mute();
        }
        if (analyticsEvent) {
          analyticsEvent();
        }
      }
    };

    window.addEventListener('message', handlePlayerEvent);

    return () => {
      window.removeEventListener('message', handlePlayerEvent);
    };
  }, [analyticsEvent]);

  const style = useMemo(
    () =>
      ({
        '--preview-url': `url("${!isPlayerInitialized && previewImageUrl ? previewImageUrl : ''}")`,
      }) as React.CSSProperties,
    [isPlayerInitialized, previewImageUrl],
  );

  return (
    <>
      <Script
        src="https://vk.com/js/api/videoplayer.js"
        strategy="afterInteractive"
      />
      {videoObject && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{__html: JSON.stringify(videoObject)}}
        />
      )}

      <iframe
        id="video_block"
        ref={iframeRef}
        className={classNames(className, styles.background)}
        data-testid={dataTestid}
        src={`${videoUrl}&autoplay=${Number(autoPlay)}&js_api=1`}
        allow="autoplay; encrypted-media; picture-in-picture; screen-wake-lock;"
        allowFullScreen
        style={style}
      />
    </>
  );
};
